import {
  BankDetailsBlockSG,
  CardDetailsBlock,
  CashPickupLocation,
  CommonButton,
  ModalFactory,
  ValidCashPayoutIdList
} from '@Components';
import React, { useCallback, useEffect, useState } from 'react';
import { change, getFormValues, reduxForm } from 'redux-form';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IStore, UserFlowReducer } from '../../../../../store.type';
import BeneSchemaBeneDetailsContainer from '../../../../../screens/beneficiary-info/index-v2';
import {
  getUserFlowReducer,
  selectBankValidationsReducer,
  selectForms,
  selectNewBeneState,
  selectTransactionDetails,
  selectUserSessionReducer,
  selectUserStatusReducer
} from '@Redux/selectors';
import { BankDetailsFormValueType, BankDetailsPropsType } from '../utils/types';
import { get, isEmpty } from 'lodash';
import { LabelTextCash, LabelTextWrapper } from '../../../../beneficiary-info/styles';
import { LabelText } from '../../../../../components/form-sections/bank-details/bankdetails.styled';
import { getCashPayoutLocationList } from '@Redux/actions/individual-user-flow';
import { INDIVIDUAL } from '@Utils/constants';
import { BENE_BANK_DETAILS_FORM_NAME, BENE_FORM, screenNames } from '../../../constants/commons';
import { scrollToError, scrollToTop } from '@Redux/actions/ui-trigger';
import { ASYNC_BLUR_FIELDS, asyncValidate, validate } from '../../../../bene-info-correction/utils';
import { ValidationState } from '../../../../bene-info-correction/interface';
import { GenericRecordType } from '../../personal-details/utils/types';
import { processBeneficiaryData } from '../../../layout/utils/methods';
import { setBeneRevampFormValues } from '@Redux/actions/new-bene-flow';
import {
  navigateHistory,
  setCurrentBeneDataAction
} from '@Redux/actions/new-bene-flow/new-bene-flow-action';
import {
  CASH_PAYOUT_TEXT_1_PART_1,
  CASH_PAYOUT_TEXT_1_PART_2,
  CASH_PAYOUT_TEXT_1_PART_3,
  CASH_PAYOUT_TEXT_2_PART_1,
  CASH_PAYOUT_TEXT_2_PART_2,
  CASH_PAYOUT_TEXT_2_PART_3,
  CNY_BANK_CHANNEL_NOTE_1,
  CNY_BANK_CHANNEL_NOTE_2,
  MONEY_GRAM_PAYOUT__TEXT_3_PART_2,
  MONEY_GRAM_PAYOUT_TEXT_2,
  MONEY_GRAM_PAYOUT_TEXT_3_PART_1,
  MONEY_GRAM_PAYOUT_TEXT_3_PART_3,
  MONEYGRAM_CASH_PAYOUT_TEXT_1_PART_1,
  MONEYGRAM_CASH_PAYOUT_TEXT_1_PART_2
} from '../utils/constants';
import { handleBeneSubmit } from '../utils/methods';

const BankDetailsForm: React.FC<BankDetailsPropsType> = (props) => {
  const {
    handleSubmit,
    initialize,
    storeValidationConfig,
    parentState: { beneState, isEditFlow, isRedirectedFromSendMoney }
  } = props;
  const dispatch = useDispatch<AppDispatch>();

  const reduxForm = useSelector(selectForms);
  const bankValidationsShowLoader = useSelector(selectBankValidationsReducer)?.show_loader;
  const newBeneStateReducer = useSelector(selectNewBeneState);
  const userStatusReducer = useSelector(selectUserStatusReducer);
  const transactionDetails = useSelector(selectTransactionDetails);
  const { timeline, screens } = useSelector(getUserFlowReducer) as UserFlowReducer;

  const {
    isBeneSchemaEnabled: isBeneSchemaApplicable,
    schemaApiResponse,
    payoutApiResponse,
    nonSchemaCountriesApiResponse,
    currentBene,
    unfilteredCurrentBene,
    currentBeneId: beneId,
    formValues: {
      account_type,
      bank_account_country,
      currency_code,
      beneficiary_name,
      company_name,
      details_selected
    }
  } = newBeneStateReducer;

  const { user_session } = useSelector(selectUserSessionReducer) || {};

  const { country_code: remitter_country } = user_session || {};

  useEffect(() => {
    if (!isEmpty(schemaApiResponse)) {
      /*
         bank_account_country, currency_code must be in current form state which will be used as
         destination_country in UPI proxy api 
       */
      dispatch(change(BENE_BANK_DETAILS_FORM_NAME, 'bank_account_country', bank_account_country));
      dispatch(change(BENE_BANK_DETAILS_FORM_NAME, 'currency_code', currency_code));
      dispatch(
        change(BENE_BANK_DETAILS_FORM_NAME, 'beneficiary_name', beneficiary_name || company_name)
      );
    }
  }, [
    schemaApiResponse,
    bank_account_country,
    dispatch,
    currency_code,
    beneficiary_name,
    company_name
  ]);

  useEffect(() => {
    if (!isBeneSchemaApplicable && !isEmpty(nonSchemaCountriesApiResponse))
      processBeneficiaryData(nonSchemaCountriesApiResponse, null, storeValidationConfig);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBeneSchemaApplicable, nonSchemaCountriesApiResponse]);

  const getBeneficiaryBankName = useCallback(() => {
    let beneficiaryBankName = '';
    switch (unfilteredCurrentBene?.routing_code_type_1) {
      case 'SWIFT':
        beneficiaryBankName =
          unfilteredCurrentBene.beneficiary_bank_name +
          '(->' +
          unfilteredCurrentBene.routing_code_value_1 +
          '<-)';

        break;
      case 'BRANCH_CODE':
        beneficiaryBankName =
          unfilteredCurrentBene.beneficiary_bank_name +
          '(->' +
          unfilteredCurrentBene.beneficiary_bank_code +
          '<-)';
        break;
      case 'IFSC':
        beneficiaryBankName = unfilteredCurrentBene.beneficiary_bank_name;
        break;
      default:
        break;
    }
    return beneficiaryBankName;
  }, [unfilteredCurrentBene]);

  useEffect(() => {
    if (isEditFlow && !isEmpty(currentBene)) {
      let beneficiaryBankName = getBeneficiaryBankName();
      initialize({
        ...currentBene,
        ...(currentBene?.beneficiary_bank_name && {
          beneficiary_bank_name: beneficiaryBankName
        })
      });
      if (isBeneSchemaApplicable) {
        const { card_token } = currentBene || {};
        if (card_token) {
          dispatch(change(BENE_BANK_DETAILS_FORM_NAME, 'card_number_disabled', true));
        }
      } else dispatch(change(BENE_BANK_DETAILS_FORM_NAME, 'is_card_number_updated', false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditFlow, currentBene, dispatch, isBeneSchemaApplicable]);

  const formValues = useSelector((state: IStore) =>
    getFormValues(BENE_BANK_DETAILS_FORM_NAME)(state)
  ) as BankDetailsFormValueType;

  const combinedFormValues = {
    ...newBeneStateReducer?.formValues,
    ...formValues
  };

  const { payment_mode_supported, is_card_number_updated } = formValues || {};

  const { is_partially_editable, is_completely_editable } = window.history.state?.usr || {};
  const isMoneyGramCashPayoutSelected =
    details_selected === 'money_cash' || details_selected === 'moneygram_cash_payout';
  const isCashPayoutSelected = details_selected === 'cash';
  const isIndividualChinaBeneWithCNYCurrency =
    account_type === INDIVIDUAL && bank_account_country === 'CN' && currency_code === 'CNY';
  const isGeoSwiftCardPaymentMethodBankChannel =
    payment_mode_supported === 'BANK_CHANNEL' ||
    (get(formValues, 'beneDetails.card_details.paymentModeSupported', '') === 'BANK_CHANNEL' &&
      (is_partially_editable || is_completely_editable) &&
      is_card_number_updated === false);

  //submit form handler
  // submit form here if address is not required else navigate to address screen
  const submitForm = async () => {
    let selectedPayoutMethod = combinedFormValues.payout_method;

    const isAddressRequiredFromPayoutApi =
      // Its for non bene schema flow
      payoutApiResponse?.find((schema: any) => {
        let payoutMethod = selectedPayoutMethod?.toUpperCase();
        if (payoutMethod === 'CARD') payoutMethod = 'VISA_DEBIT_CARD';
        else if (payoutMethod === 'CASH') payoutMethod = 'MONEYGRAM_CASH_PAYOUT';
        else if (payoutMethod === 'LOCAL') payoutMethod = 'BANK';

        return schema.routing_config_type?.toUpperCase() === payoutMethod;
      })?.is_address_required ?? false;

    const isAddressRequired =
      // Bene schema flow
      schemaApiResponse?.address?.required === true || isAddressRequiredFromPayoutApi;

    if (isEditFlow) {
      dispatch(setCurrentBeneDataAction({ ...currentBene, ...combinedFormValues }));
    }
    dispatch(setBeneRevampFormValues(combinedFormValues));

    if (!isAddressRequired) {
      const config_id = currentBene?.beneficiary_routing_config_id;
      const additionalData = {
        user_session,
        userStatusReducer,
        transactionDetails,
        screens,
        timeline,
        config_id,
        is_partially_editable,
        is_completely_editable,
        beneId,
        bankDetails: beneState,
        isEditFlow
      };
      await handleBeneSubmit(
        combinedFormValues,
        isBeneSchemaApplicable,
        '',
        dispatch,
        BENE_FORM,
        additionalData,
        isRedirectedFromSendMoney,
        isAddressRequired,
        payoutApiResponse
      );

      dispatch(scrollToTop());
    } else {
      dispatch(
        navigateHistory({
          type: 'PUSH_NAVIGATION_HISTORY',
          screen: screenNames.recipient_address.name
        })
      );

      dispatch(scrollToTop());
    }
  };

  const renderBeneSchemaBankDetails = () => {
    if (isEmpty(schemaApiResponse)) return null;
    return (
      <BeneSchemaBeneDetailsContainer
        beneConfig={payoutApiResponse}
        formValues={combinedFormValues}
        dispatch={dispatch}
        formName={BENE_BANK_DETAILS_FORM_NAME}
        isNewBeneAddFlow={true}
        currentBeneDetails={currentBene}
        isCompleteEditable={is_completely_editable}
        isPartiallyEditable={is_partially_editable}
        isSingleRowField={true}
        onCountryCurrencyPairChange={storeValidationConfig}
      />
    );
  };

  const showValidIds = async () => {
    // @ts-ignore
    return await ModalFactory.builder(
      (onClose: () => void) => <ValidCashPayoutIdList onClose={onClose} />,
      'center-modal',
      true,
      { disAbleCloseAnimation: true }
    );
  };

  const showPickupLocations = async () => {
    let locationList = await dispatch(getCashPayoutLocationList(bank_account_country));
    if (locationList?.data?.success) {
      // @ts-ignore
      return await ModalFactory.builder(
        (onClose: () => void) => {
          return <CashPickupLocation onClose={onClose} locationData={locationList?.data?.data} />;
        },
        'center-modal',
        true,
        { disAbleCloseAnimation: true }
      );
    }
  };

  const renderNonBeneSchemaBankDetails = () => {
    if (
      account_type &&
      bank_account_country &&
      currency_code &&
      !isEmpty(nonSchemaCountriesApiResponse)
    )
      return (
        <>
          {currency_code && beneState.additionalFields && !isEmpty(beneState) && (
            <BankDetailsBlockSG
              renderFields={beneState.additionalFields}
              formValues={combinedFormValues}
              renderAdditionalField={true}
              dispatch={dispatch}
              currency_code={currency_code}
              formName={BENE_BANK_DETAILS_FORM_NAME}
              isSingleRowField={true}
              hideRecipientReceivingOptions={true}
              isNewBeneAddFlow={true}
            />
          )}
          {beneState.debit_card_details && !beneState.current_bank_fields && (
            <CardDetailsBlock
              fields={beneState.current_bank_fields}
              renderFields={beneState.current_bank_fields}
              debitCardFields={beneState.debit_card_details}
              show_loader={bankValidationsShowLoader}
              formValues={combinedFormValues}
              dispatch={dispatch}
              currency_code={currency_code || 'INR'}
              formName={BENE_BANK_DETAILS_FORM_NAME}
              countryCode={remitter_country}
              bank_account_country={bank_account_country}
              isBlock={true}
              bank_validations={beneState.bank_validations}
              debit_validations={beneState.debit_validations}
              isPartiallyEditable={beneState.is_partially_editable}
              isCompleteEditable={beneState.is_completely_editable}
              errors={{
                ...reduxForm?.[BENE_BANK_DETAILS_FORM_NAME]?.syncErrors,
                ...reduxForm?.[BENE_BANK_DETAILS_FORM_NAME]?.asyncErrors
              }}
              isNewBeneAddFlow={true}
              isSingleRowField={true}
            />
          )}
          {((beneState.current_bank_fields && beneState.debit_card_details) ||
            beneState.current_bank_fields ||
            beneState.beneWalletDetails ||
            beneState.beneCashPayoutDetails) &&
            ((bank_fields, debit_card_fields, cash_payout_details) => {
              if (currency_code && (bank_fields || cash_payout_details)) {
                return (
                  <>
                    <BankDetailsBlockSG
                      fields={bank_fields}
                      renderFields={bank_fields}
                      debitCardFields={debit_card_fields}
                      show_loader={bankValidationsShowLoader}
                      formValues={combinedFormValues}
                      dispatch={dispatch}
                      currency_code={currency_code || 'INR'}
                      formName={BENE_BANK_DETAILS_FORM_NAME}
                      countryCode={remitter_country}
                      bank_account_country={bank_account_country}
                      isBlock={true}
                      bank_validations={beneState.bank_validations}
                      debit_validations={beneState.debit_validations}
                      wallet_validations={beneState.wallet_validations}
                      isPartiallyEditable={beneState.is_partially_editable}
                      isCompleteEditable={beneState.is_completely_editable}
                      errors={
                        reduxForm &&
                        reduxForm[BENE_BANK_DETAILS_FORM_NAME] && {
                          ...reduxForm[BENE_BANK_DETAILS_FORM_NAME].syncErrors,
                          ...reduxForm[BENE_BANK_DETAILS_FORM_NAME].asyncErrors
                        }
                      }
                      beneCashPayoutDetails={beneState.beneCashPayoutDetails}
                      beneWalletDetails={beneState.beneWalletDetails?.BANK_DETAIL}
                      isSingleRowField={true}
                      hideRecipientReceivingOptions={true}
                      onCountryCurrencyPairChange={storeValidationConfig}
                      isNewBeneAddFlow={true}
                    />
                  </>
                );
              }
            })(
              beneState.current_bank_fields,
              beneState.debit_card_details || null,
              beneState.beneCashPayoutDetails || null
            )}
          {isIndividualChinaBeneWithCNYCurrency && isGeoSwiftCardPaymentMethodBankChannel && (
            <LabelTextWrapper>
              {/* @ts-ignore */}
              <LabelText id="cny_bank_channel_note_1" paddingTop="10px;" $isSingleRowField={true}>
                {CNY_BANK_CHANNEL_NOTE_1}
              </LabelText>
              <LabelText id="cny_bank_channel_note_2">{CNY_BANK_CHANNEL_NOTE_2}</LabelText>
            </LabelTextWrapper>
          )}
          {isCashPayoutSelected && (
            <LabelTextWrapper>
              <LabelTextCash id="cash-payout-text-1">
                <div>
                  {CASH_PAYOUT_TEXT_1_PART_1}
                  <span onClick={showPickupLocations}>{CASH_PAYOUT_TEXT_1_PART_2}</span>
                </div>
                <div>{CASH_PAYOUT_TEXT_1_PART_3}</div>
              </LabelTextCash>
              <LabelTextCash id="cash-payout-text-2">
                <div>{CASH_PAYOUT_TEXT_2_PART_1}</div>
                <div>
                  {CASH_PAYOUT_TEXT_2_PART_2}
                  <span onClick={showValidIds}> {CASH_PAYOUT_TEXT_2_PART_3}</span>
                </div>
              </LabelTextCash>
            </LabelTextWrapper>
          )}
          {isMoneyGramCashPayoutSelected && (
            <LabelTextWrapper>
              {/* @ts-ignore */}
              <LabelTextCash paddingTop={'15px;'} id="money-gram-cash-payout-text-1">
                <div>
                  {MONEYGRAM_CASH_PAYOUT_TEXT_1_PART_1}
                  <span>
                    <a
                      href="https://www.moneygram.com/mgo/us/en/locations"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {MONEYGRAM_CASH_PAYOUT_TEXT_1_PART_2}
                    </a>
                  </span>
                </div>
              </LabelTextCash>
              {/* @ts-ignore */}
              <LabelTextCash paddingTop={'0px;'} id="money-gram-cash-payout-text-2">
                <div>{MONEY_GRAM_PAYOUT_TEXT_2}</div>
              </LabelTextCash>
              {/* @ts-ignore */}
              <LabelTextCash paddingTop={'0px;'} id="money-gram-cash-payout-text-3">
                <div>
                  {MONEY_GRAM_PAYOUT_TEXT_3_PART_1}
                  <span>
                    <a href="/info/cash-pickup" target="_blank">
                      {MONEY_GRAM_PAYOUT__TEXT_3_PART_2}
                    </a>
                  </span>
                  {MONEY_GRAM_PAYOUT_TEXT_3_PART_3}
                </div>{' '}
              </LabelTextCash>
            </LabelTextWrapper>
          )}
        </>
      );
    else return null;
  };

  return (
    <div className="child-component-container__content-container">
      <form onSubmit={handleSubmit(submitForm)}>
        {isBeneSchemaApplicable ? renderBeneSchemaBankDetails() : renderNonBeneSchemaBankDetails()}
        <CommonButton
          margin={isBeneSchemaApplicable ? '16px 0 0 0' : '48px 0 0 0'}
          text="Next"
          width={450}
        />
      </form>
    </div>
  );
};

const BankDetailsWrapper = ({ children, ...props }: { children: React.ReactElement }) => {
  const [validationState, setValidationState] = useState<ValidationState>({
    beneficiary_bank_validations: null,
    bene_schema_validation: null
  });

  const { bank_account_country, country_of_business, currency_code, account_type } =
    useSelector(selectNewBeneState)?.formValues || {};

  // This is the key function that provides validation config
  const getValidationConfig = useCallback(() => {
    const { beneficiary_bank_validations, bene_schema_validation } = validationState;
    return {
      beneficiary_bank_validations,
      bene_schema_validation
    };
  }, [validationState]);

  // Function to update validation config
  const storeValidationConfig = useCallback(
    async (BANK_DETAIL: GenericRecordType, beneSchema?: GenericRecordType) => {
      const beneficiary_bank_validations = { ...BANK_DETAIL };
      const bene_schema_validation = { ...beneSchema };
      setValidationState({
        beneficiary_bank_validations,
        bene_schema_validation
      });
    },
    []
  );

  // Pass the validation functions to children
  return React.cloneElement(children, {
    ...props,
    getValidationConfig,
    storeValidationConfig,
    validationState,
    reduxData: {
      destination_country: bank_account_country,
      bank_account_country,
      country_of_business,
      currency_code,
      account_type,
      formName: BENE_BANK_DETAILS_FORM_NAME
    }
  });
};

const BankDetailsFormWithRedux = reduxForm({
  form: BENE_BANK_DETAILS_FORM_NAME,
  validate: (values, props) => validate(props.getValidationConfig, props.reduxData)(values),
  asyncValidate: (values, dispatch, props) => asyncValidate(props.reduxData)(values, dispatch),
  asyncBlurFields: ASYNC_BLUR_FIELDS,
  onSubmitFail: scrollToError,
  destroyOnUnmount: false
})(BankDetailsForm);

const BankDetailsReduxForm = (props: { props: GenericRecordType }) => {
  return (
    <BankDetailsWrapper>
      <BankDetailsFormWithRedux {...props} />
    </BankDetailsWrapper>
  );
};

export default BankDetailsReduxForm;
